import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Field } from '@promtver/ui-kit';
import type { NotificationTemplate } from '@promtver/contracts';

import { getNotificationTemplates, saveNotificationTemplates } from '../../api/admin';
import { errorMessage } from '../../api/errors';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { ru } from '../../i18n/ru';

/**
 * Шаблоны уведомлений (FR-NTF-03): редактирование текстов и настройка
 * задержки отправки для каждого события. Служебные уведомления по действующей
 * брони отключению не подлежат (FR-NTF-04) — их задержка не редактируется.
 */
export function NotificationsPage() {
  const queryClient = useQueryClient();
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'notification-templates'],
    queryFn: getNotificationTemplates,
  });

  useEffect(() => {
    if (query.data) {
      setTemplates(query.data);
    }
  }, [query.data]);

  const saveMutation = useMutation({
    mutationFn: () => saveNotificationTemplates(templates),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'notification-templates'] });
    },
    onError: (cause) => {
      setSaved(false);
      setError(errorMessage(cause, ru.common.unknownError));
    },
  });

  const update = (code: string, patch: Partial<NotificationTemplate>) => {
    setSaved(false);
    setTemplates((previous) =>
      previous.map((item) => (item.code === code ? { ...item, ...patch } : item)),
    );
  };

  return (
    <AppShell title={ru.notifications.title} subtitle={ru.notifications.subtitle}>
      <AsyncState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={templates.length === 0}
        emptyTitle={ru.notifications.empty}
        onRetry={() => void query.refetch()}
      >
        <ul className="flex flex-col gap-3">
          {templates.map((template) => (
            <li key={template.code} className="rounded-card border border-line p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <strong className="text-sm">{template.title}</strong>
                {template.mandatory ? (
                  <Badge tone="steel">{ru.notifications.mandatory}</Badge>
                ) : null}
              </div>

              <div className="flex flex-col gap-3">
                <Field
                  label={ru.notifications.body}
                  value={template.body}
                  onChange={(value) => update(template.code, { body: value })}
                />
                <Field
                  label={ru.notifications.delay}
                  value={String(Math.round(template.delaySec / 60))}
                  onChange={(value) =>
                    update(template.code, { delaySec: Math.max(0, (Number(value) || 0) * 60) })
                  }
                  inputMode="numeric"
                  disabled={template.mandatory}
                />
              </div>

              {template.mandatory ? (
                <p className="mt-2 text-xs text-muted">{ru.notifications.mandatoryHint}</p>
              ) : null}
            </li>
          ))}
        </ul>

        <div className="mt-4 flex flex-col gap-2">
          <Button tone="accent" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            {saveMutation.isPending ? ru.common.saving : ru.notifications.saveAll}
          </Button>
          {saved ? <p className="text-xs text-success">{ru.notifications.saved}</p> : null}
          {error ? (
            <p role="alert" className="text-xs text-danger">
              {error}
            </p>
          ) : null}
        </div>
      </AsyncState>
    </AppShell>
  );
}
